import React, { useState } from 'react';
import { Icon } from 'semantic-ui-react';

// Any heading block within a section (not just level-3 ones) counts as a
// navigable subheading — some sections only emit one-off level-2 headings
// rather than the repeated level-3 pattern used for per-theme headings.
const getSubheadings = (section) => (section.blocks || []).filter(
  (block) => block.type === 'heading' && block.level >= 2,
);

const SectionToc = ({
  sections, selectedSectionId, onSelectSection, onSelectSubheading,
}) => {
  const [expandedSectionIds, setExpandedSectionIds] = useState(
    () => new Set(selectedSectionId ? [selectedSectionId] : []),
  );

  const expandSection = (sectionId) => {
    setExpandedSectionIds((prev) => new Set(prev).add(sectionId));
  };

  const toggleExpanded = (sectionId) => {
    setExpandedSectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const handleSelectSection = (sectionId, subheadings) => {
    onSelectSection(sectionId);
    if (subheadings.length > 0) expandSection(sectionId);
  };

  return (
    // Plain markup with explicit sizes on purpose — Semantic UI's Accordion
    // classes ("title"/"content") are generic enough to collide with other
    // global styles in this app, which was blowing up the font size here.
    <div style={{ fontSize: '0.85rem' }}>
      {sections.map((section) => {
        const subheadings = getSubheadings(section);
        const isExpanded = expandedSectionIds.has(section.id);
        const isSelected = section.id === selectedSectionId;

        return (
          <div key={section.id} style={{ borderBottom: '1px solid rgba(34, 36, 38, 0.1)' }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => handleSelectSection(section.id, subheadings)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleSelectSection(section.id, subheadings);
              }}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.35em',
                padding: '0.6em 0',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: isSelected ? 700 : 500,
                color: isSelected ? '#1a73e8' : 'rgba(0, 0, 0, 0.87)',
                lineHeight: 1.3,
              }}
            >
              {subheadings.length > 0 ? (
                <Icon
                  name={isExpanded ? 'chevron down' : 'chevron right'}
                  style={{ fontSize: '0.7rem', marginTop: '0.25em' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(section.id);
                  }}
                />
              ) : (
                <span style={{ width: '0.7rem', flexShrink: 0 }} />
              )}
              <span>{section.title}</span>
            </div>
            {subheadings.length > 0 && isExpanded && (
              <ul style={{ listStyle: 'none', margin: 0, padding: '0 0 0.5em 0' }}>
                {subheadings.map((heading) => (
                  <li key={heading.id}>
                    <a
                      href={`#report-block-${heading.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        onSelectSubheading(section.id, heading.id);
                      }}
                      style={{
                        display: 'block',
                        fontSize: '0.75rem',
                        color: 'rgba(0, 0, 0, 0.6)',
                        padding: '0.3em 0 0.3em 1.5em',
                        textDecoration: 'none',
                        lineHeight: 1.3,
                      }}
                    >
                      {heading.text}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SectionToc;
